<template>
  <Transition name="toast" appear>
    <div 
      v-if="visible"
      :class="['toast', `toast--${type}`, { 'toast--dismissible': dismissible }]"
      role="alert"
      @click="handleClick"
    >
      <div class="toast-icon">
        <span v-if="type === 'success'">✅</span>
        <span v-else-if="type === 'error'">❌</span>
        <span v-else-if="type === 'warning'">⚠️</span>
        <span v-else-if="type === 'info'">ℹ️</span>
        <span v-else>📢</span>
      </div>
      
      <div class="toast-content">
        <div v-if="title" class="toast-title">{{ title }}</div>
        <div class="toast-message">{{ message }}</div>
      </div>
      
      <button 
        v-if="dismissible"
        @click.stop="dismiss"
        class="toast-close"
        aria-label="Close notification"
      >
        ×
      </button>
      
      <div 
        v-if="autoClose && duration > 0"
        class="toast-progress"
        :style="{ animationDuration: `${duration}ms` }"
      ></div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    required: true
  },
  duration: {
    type: Number,
    default: 5000
  },
  autoClose: {
    type: Boolean,
    default: true
  },
  dismissible: {
    type: Boolean,
    default: true
  },
  persistent: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close'])

const visible = ref(true)
let timeoutId = null

const dismiss = () => {
  visible.value = false
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
  setTimeout(() => {
    emit('close')
  }, 300) // Wait for exit animation
}

const handleClick = () => {
  if (props.dismissible && !props.persistent) {
    dismiss()
  }
}

onMounted(() => {
  if (props.autoClose && props.duration > 0 && !props.persistent) {
    timeoutId = setTimeout(() => {
      dismiss()
    }, props.duration)
  }
})

onUnmounted(() => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
})
</script>

<style scoped>
.toast {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  max-width: 400px;
  min-width: 300px;
  cursor: default;
  overflow: hidden;
}

.toast--dismissible {
  cursor: pointer;
}

.toast--success {
  background: rgba(212, 237, 218, 0.95);
  border: 1px solid #c3e6cb;
  color: #155724;
}

.toast--error {
  background: rgba(248, 215, 218, 0.95);
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.toast--warning {
  background: rgba(255, 243, 205, 0.95);
  border: 1px solid #ffeaa7;
  color: #856404;
}

.toast--info {
  background: rgba(209, 236, 241, 0.95);
  border: 1px solid #bee5eb;
  color: #0c5460;
}

.toast-icon {
  font-size: 1.2em;
  flex-shrink: 0;
  margin-top: 2px;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 0.95em;
}

.toast-message {
  font-size: 0.9em;
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-close {
  background: none;
  border: none;
  font-size: 1.5em;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toast-close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.1);
}

.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: currentColor;
  opacity: 0.6;
  animation: progress linear forwards;
  transform-origin: left;
}

@keyframes progress {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

/* Transition animations */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .toast {
    max-width: calc(100vw - 32px);
    min-width: auto;
    margin: 0 16px;
  }
}
</style> 